export type InteractionRect = { left: number; top: number; right: number; bottom: number };
export type InteractionRegion = InteractionRect & {
  planObjectIds?: readonly string[];
  ellipse?: { cx: number; cy: number; rx: number; ry: number };
};

/** Circular symbols cut out every route, including their own; other own plan controls stay visible. */
export function connectionVisualRegions(regions: readonly InteractionRegion[], endpoints: readonly {surface: string; anchorObjectId?: string}[]): InteractionRegion[] {
  const owners = new Set(endpoints.filter(endpoint => endpoint.surface === "drawing").map(endpoint => endpoint.anchorObjectId));
  return regions.filter(region => region.ellipse || !region.planObjectIds?.some(id => owners.has(id)))
    .sort((a, b) => Number(!!a.ellipse) - Number(!!b.ellipse));
}

/** Visible screen bounds of a preferred control, including scrolling ancestor clips. */
export function measureInteractionRegion(element: Element): InteractionRegion | undefined {
  const style = getComputedStyle(element);
  if (style.visibility === "hidden" || style.display === "none") return;
  const circle = element.querySelector?.('[data-connection-cutout="circle"]');
  const bounds = (circle ?? element).getBoundingClientRect();
  let left = Math.max(0, bounds.left), top = Math.max(0, bounds.top);
  let right = Math.min(window.innerWidth, bounds.right), bottom = Math.min(window.innerHeight, bounds.bottom);
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const computed = getComputedStyle(parent);
    const clipX = /hidden|clip|scroll|auto/.test(computed.overflowX);
    const clipY = /hidden|clip|scroll|auto/.test(computed.overflowY);
    if (!clipX && !clipY) continue;
    const clip = parent.getBoundingClientRect();
    if (clipX) { left = Math.max(left, clip.left); right = Math.min(right, clip.right); }
    if (clipY) { top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom); }
  }
  if (right > left && bottom > top) {
    const region: InteractionRegion = { left, top, right, bottom };
    if (circle) region.ellipse = { cx: (bounds.left + bounds.right) / 2, cy: (bounds.top + bounds.bottom) / 2,
      rx: (bounds.right - bounds.left) / 2, ry: (bounds.bottom - bounds.top) / 2 };
    return region;
  }
}

/** Disjoint rectangles for the union: overlapping controls must never reopen a hit area. */
export function unionInteractionRegions(regions: readonly InteractionRect[], bounds: InteractionRect): InteractionRect[] {
  const clipped = regions.map(rect => ({left: Math.max(bounds.left, rect.left), top: Math.max(bounds.top, rect.top),
    right: Math.min(bounds.right, rect.right), bottom: Math.min(bounds.bottom, rect.bottom)}))
    .filter(rect => [rect.left, rect.top, rect.right, rect.bottom].every(Number.isFinite) && rect.right > rect.left && rect.bottom > rect.top);
  const xs = [...new Set(clipped.flatMap(rect => [rect.left, rect.right]))].sort((a, b) => a - b);
  const result: InteractionRect[] = [];
  for (let index = 1; index < xs.length; index++) {
    const left = xs[index - 1], right = xs[index];
    const spans = clipped.filter(rect => rect.left < right && rect.right > left).sort((a, b) => a.top - b.top);
    let current: InteractionRect | undefined;
    for (const span of spans) {
      if (current && span.top <= current.bottom) current.bottom = Math.max(current.bottom, span.bottom);
      else { current = {left, right, top: span.top, bottom: span.bottom}; result.push(current); }
    }
  }
  return result;
}

export function interactionRectPath(rect: InteractionRect): string {
  return `M${rect.left} ${rect.top}H${rect.right}V${rect.bottom}H${rect.left}Z`;
}

/** The visible circle supplies the cutout, independently of its icon or surrounding labels. */
export function interactionRegionPath(region: InteractionRegion): string {
  const ellipse = region.ellipse;
  if (!ellipse) return interactionRectPath(region);
  const { cx, cy, rx, ry } = ellipse;
  return `M${cx - rx} ${cy}a${rx} ${ry} 0 1 0 ${rx * 2} 0a${rx} ${ry} 0 1 0 ${-rx * 2} 0Z`;
}

/** Use with SVG clip-rule=evenodd. Masks alone do not suppress native pointer hits. */
export function connectionInteractionClip(bounds: InteractionRect, union: readonly InteractionRect[]): string {
  return interactionRectPath(bounds) + union.map(interactionRectPath).join("");
}
